/**
 * Objetivos de capítulo (M3, AC-M3-08). Além de "sobreviver + Golemita" (cap.1), os capítulos 2–4
 * têm objetivos: MINERAÇÃO (minerar nós ficando perto) e ESCOLTA (levar um NPC ao destino, protegendo-o).
 * Determinístico e SEM rng (posições fixas) → não desloca o stream; cap.1 (sem objetivo) fica intacto.
 *   cap.2 = mineração · cap.3 = escolta · cap.4 = mineração + escolta (completo).
 */
import type { EnemySim, HeroSim } from './types.ts';

export interface MiningNode {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  mined: boolean;
}
export interface EscortNpc {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  alive: boolean;
}
export interface Objetivo {
  mineracao?: { nodes: MiningNode[]; feitos: number; meta: number };
  escolta?: { npc: EscortNpc; destino: { x: number; y: number }; chegou: boolean };
  completo: boolean;
}

export type StatusObjetivo = 'ativo' | 'completo' | 'falhou';

const MINE_RAIO = 90; // px: herói perto do nó minera
const MINE_DPS = 70; // dano/s ao nó
const NODE_HP = 140;
const ESCORT_RAIO = 170; // px: NPC só avança com herói por perto
const ESCORT_SPEED = 80; // px/s
const DESTINO_RAIO = 60; // px: NPC "chegou"
const NPC_HP = 800;
const NPC_HIT_RAIO = 30; // px: inimigo colado no NPC o fere
const NPC_DANO_TICK = 4; // dano por inimigo colado, por tick

const dist2 = (ax: number, ay: number, bx: number, by: number): number =>
  (ax - bx) * (ax - bx) + (ay - by) * (ay - by);

/** cria o objetivo do capítulo (cap.≤1 → null: fluxo Golemita clássico, sim base intacta). */
export function criarObjetivo(chapter: number, arenaW: number, arenaH: number): Objetivo | null {
  if (chapter <= 1) return null;
  const cx = arenaW / 2;
  const cy = arenaH / 2;
  const node = (x: number, y: number): MiningNode => ({ x, y, hp: NODE_HP, maxHp: NODE_HP, mined: false });
  const mineracao = { nodes: [node(cx - 460, cy), node(cx + 460, cy), node(cx, cy - 340)], feitos: 0, meta: 3 };
  const escolta = {
    npc: { x: cx - 260, y: cy + 260, hp: NPC_HP, maxHp: NPC_HP, alive: true },
    destino: { x: cx + 620, y: cy - 300 },
    chegou: false,
  };
  // capítulos 2–12: objetivo rotativo (mineração / escolta / ambos). Cap.1 = boss clássico (Golemita).
  // O boss puro fica intanque a partir do cap.8 (bot não vence por DPS); objetivo mantém "completável"
  // enquanto a dificuldade (pressão de horda) sobe monotônica com o capítulo (AC-M4-01).
  const ambos = { mineracao: { ...mineracao, meta: 2, nodes: mineracao.nodes.slice(0, 2) }, escolta, completo: false };
  switch ((chapter - 2) % 3) {
    case 0:
      return { mineracao, completo: false }; // 2, 5, 8, 11
    case 1:
      return { escolta, completo: false }; // 3, 6, 9, 12
    default:
      return ambos; // 4, 7, 10
  }
}

/** avança o objetivo um tick; devolve o status (tick decide vitória/derrota). Muta `obj`. */
export function atualizarObjetivo(
  obj: Objetivo,
  heroes: readonly HeroSim[],
  enemies: readonly EnemySim[],
  dt: number,
): StatusObjetivo {
  const vivos = heroes.filter((h) => h.alive && !h.down);

  if (obj.mineracao) {
    const m = obj.mineracao;
    for (const nd of m.nodes) {
      if (nd.mined) continue;
      const perto = vivos.some((h) => dist2(h.x, h.y, nd.x, nd.y) <= MINE_RAIO * MINE_RAIO);
      if (perto) {
        nd.hp -= MINE_DPS * dt;
        if (nd.hp <= 0) {
          nd.hp = 0;
          nd.mined = true;
          m.feitos += 1;
        }
      }
    }
  }

  if (obj.escolta) {
    const e = obj.escolta;
    if (e.npc.alive && !e.chegou) {
      // dano dos inimigos colados no NPC (protegê-lo é parte da escolta)
      let colados = 0;
      for (const en of enemies) {
        if (en.alive && !en.boss && dist2(en.x, en.y, e.npc.x, e.npc.y) <= NPC_HIT_RAIO * NPC_HIT_RAIO) colados += 1;
      }
      e.npc.hp -= colados * NPC_DANO_TICK;
      if (e.npc.hp <= 0) {
        e.npc.hp = 0;
        e.npc.alive = false;
        return 'falhou'; // NPC morreu → capítulo perdido
      }
      // avança rumo ao destino só com um herói por perto (a "escolta")
      const escoltado = vivos.some((h) => dist2(h.x, h.y, e.npc.x, e.npc.y) <= ESCORT_RAIO * ESCORT_RAIO);
      if (escoltado) {
        const dx = e.destino.x - e.npc.x;
        const dy = e.destino.y - e.npc.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        e.npc.x += (dx / len) * ESCORT_SPEED * dt;
        e.npc.y += (dy / len) * ESCORT_SPEED * dt;
        if (dist2(e.npc.x, e.npc.y, e.destino.x, e.destino.y) <= DESTINO_RAIO * DESTINO_RAIO) e.chegou = true;
      }
    }
  }

  const mineracaoOk = !obj.mineracao || obj.mineracao.feitos >= obj.mineracao.meta;
  const escoltaOk = !obj.escolta || obj.escolta.chegou;
  obj.completo = mineracaoOk && escoltaOk;
  return obj.completo ? 'completo' : 'ativo';
}
